import type { InvitationPreview } from '@sirius/contracts/admin'
import {
  ALREADY_A_MEMBER,
  INVITATION_HEADER,
  MAX_NAME_LENGTH,
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
} from '@sirius/contracts/admin'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import type { FormEvent, ReactNode } from 'react'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useParams, useSearchParams } from 'react-router'
import { useAdminClient } from '~/api/adminClientContext'
import type { AdminAuthClient } from '~/auth/authClient'
import { AuthCard } from '~/components/refineUi/form/authCard'
import { InputPassword } from '~/components/refineUi/form/inputPassword'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { formatDateTime } from '~/i18n/format'
import { cn } from '~/lib/utils'
import { FORGOT_PASSWORD_PATH } from '~/providers/authProvider'
import { errorMessage } from '~/providers/errors'
import { ME_QUERY_KEY } from '~/providers/sessionQueries'
import { useBaseDomain } from '~/tenancy/activeTenant'
import { tenantOrigin } from '~/tenancy/tenantHost'
import type { AcceptInvitationView } from './acceptInvitationView'
import { acceptInvitationView } from './acceptInvitationView'

/** A refusal of the auth API, as Better Auth's client returns it. */
class AuthApiError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
  ) {
    super(message)
    this.name = 'AuthApiError'
  }
}

/** Throws the error of a Better Auth client call, if it failed. */
function orThrow<Data>(
  result: {
    data: Data | null
    error: { message?: string; code?: string; statusText?: string } | null
  },
  t: TFunction,
) {
  if (result.error !== null) {
    const { message, code, statusText } = result.error
    throw new AuthApiError(
      message ?? statusText ?? t('auth.invitation.requestFailed'),
      code,
    )
  }
  return result.data
}

const alertClass = cn('text-sm', 'text-destructive', 'mt-6')
const noteClass = cn('text-sm', 'mt-6')
const linkClass = cn('text-sm', 'text-blue-600', 'dark:text-blue-400')

function Field({ children }: { children: ReactNode }) {
  return (
    <div className={cn('flex', 'flex-col', 'gap-2', 'mt-6')}>{children}</div>
  )
}

/** The invitation's email, which the invitee signs up or in with. */
function EmailField({ email }: { email: string }) {
  const { t } = useTranslation()
  return (
    <Field>
      <Label htmlFor="email">{t('auth.invitation.email')}</Label>
      <Input
        id="email"
        type="email"
        value={email}
        readOnly
        aria-readonly
      />
    </Field>
  )
}

/** What a closed invitation's page says. */
function closedOf(
  kind: 'expired' | 'canceled' | 'rejected' | 'accepted' | 'notFound',
  preview: InvitationPreview | undefined,
  t: TFunction,
): { description: string; text: string } {
  const key = `auth.invitation.closed.${kind}`
  return {
    description: t(`${key}.description`),
    text:
      kind === 'expired'
        ? t(`${key}.text`, { date: formatDateTime(preview!.expiresAt) })
        : kind === 'accepted'
          ? t(`${key}.text`, {
              email: preview!.email,
              tenantName: preview!.tenantName,
            })
          : t(`${key}.text`),
  }
}

/**
 * Where an invitation's mailed link lands
 * (`/accept-invitation/<id>?token=…`, on the tenant's host): the tenant and
 * the role, then sign-up (a new email) or sign-in (an existing account) and
 * accept, or accept right away when signed in with the invitation's email.
 * The email is the invitation's, fixed. Once joined it opens the tenant on
 * its host with a full page load, so every cached read starts over. Works
 * signed in or out, outside the tenant gate (the invitee may be a member of
 * no tenant yet).
 */
export function AcceptInvitationPage({
  authClient,
}: {
  authClient: AdminAuthClient
}) {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const client = useAdminClient()
  const queryClient = useQueryClient()
  const baseDomain = useBaseDomain()
  const hasLink = token !== null && token !== ''

  const preview = useQuery({
    queryKey: ['invitationPreview', id, token],
    queryFn: () => client.invitations.preview({ id, token: token ?? '' }),
    enabled: hasLink,
    retry: false,
  })
  const session = useQuery({
    queryKey: ['invitationSession'],
    queryFn: async () => {
      const data = orThrow(await authClient.getSession(), t)
      return data === null ? null : { email: data.user.email }
    },
    retry: false,
  })

  const view = acceptInvitationView({
    token,
    preview: preview.data,
    previewError: preview.error,
    // a failed session read is a signed-out visitor
    sessionUser: session.isError ? null : session.data,
  })

  const link = { [INVITATION_HEADER]: `${id}.${token ?? ''}` }
  const join = useMutation({
    mutationFn: async (
      step:
        | { kind: 'accept' }
        | { kind: 'signIn'; password: string }
        | {
            kind: 'signUp'
            name: string
            password: string
          },
    ) => {
      const email = preview.data!.email
      if (step.kind === 'signUp') {
        orThrow(
          await authClient.signUp.email({
            email,
            name: step.name,
            password: step.password,
            fetchOptions: { headers: link },
          }),
          t,
        )
      } else if (step.kind === 'signIn') {
        orThrow(
          await authClient.signIn.email({ email, password: step.password }),
          t,
        )
      }
      orThrow(
        await authClient.organization.acceptInvitation({
          invitationId: id,
          fetchOptions: { headers: link },
        }),
        t,
      )
    },
    onSuccess: () => {
      window.location.assign(
        `${tenantOrigin(preview.data!.tenantSlug, baseDomain, window.location)}/`,
      )
    },
    // signed up or in but not joined: the page shows where that left them
    onError: () => Promise.all([session.refetch(), preview.refetch()]),
  })
  const signOut = useMutation({
    mutationFn: async () => {
      orThrow(await authClient.signOut(), t)
    },
    onSuccess: async () => {
      join.reset()
      await session.refetch()
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
    },
  })

  return (
    <AcceptInvitationCard
      view={view}
      joinError={join.error}
      isJoining={join.isPending || join.isSuccess}
      onSignUp={(name, password) =>
        join.mutate({ kind: 'signUp', name, password })
      }
      onSignIn={(password) => join.mutate({ kind: 'signIn', password })}
      onAccept={() => join.mutate({ kind: 'accept' })}
      onSignOut={() => signOut.mutate()}
      isSigningOut={signOut.isPending}
      signOutError={signOut.error}
      tenantUrl={
        view.kind === 'loading' ||
        view.kind === 'notFound' ||
        view.kind === 'error'
          ? undefined
          : `${tenantOrigin(view.preview.tenantSlug, baseDomain, window.location)}/`
      }
    />
  )
}

function JoinError({
  error,
  tenantUrl,
}: {
  error: unknown
  tenantUrl: string | undefined
}) {
  const { t } = useTranslation()
  if (error === null) {
    return null
  }
  if (error instanceof AuthApiError && error.code === ALREADY_A_MEMBER) {
    return (
      <p
        role="alert"
        className={alertClass}
      >
        <Trans
          i18nKey="auth.invitation.alreadyMember"
          components={[
            <a
              key="open"
              href={tenantUrl}
              className={linkClass}
            />,
          ]}
        />
      </p>
    )
  }
  return (
    <p
      role="alert"
      className={alertClass}
    >
      {t('auth.invitation.joinFailed', { message: errorMessage(error) })}
    </p>
  )
}

/** The page for each state of `acceptInvitationView`. */
function AcceptInvitationCard({
  view,
  joinError,
  isJoining,
  onSignUp,
  onSignIn,
  onAccept,
  onSignOut,
  isSigningOut,
  signOutError,
  tenantUrl,
}: {
  view: AcceptInvitationView
  joinError: unknown
  isJoining: boolean
  onSignUp: (name: string, password: string) => void
  onSignIn: (password: string) => void
  onAccept: () => void
  onSignOut: () => void
  isSigningOut: boolean
  signOutError: unknown
  tenantUrl: string | undefined
}) {
  const { t } = useTranslation()
  const title = t('auth.invitation.title')
  switch (view.kind) {
    case 'loading':
      return (
        <AuthCard
          title={title}
          description={t('auth.invitation.loadingDescription')}
        >
          <p
            role="status"
            className={cn('text-sm', 'text-muted-foreground', 'mt-6')}
          >
            {t('auth.invitation.loading')}
          </p>
        </AuthCard>
      )
    case 'error':
      return (
        <AuthCard
          title={title}
          description={t('auth.invitation.errorDescription')}
        >
          <p
            role="alert"
            className={alertClass}
          >
            {errorMessage(view.error)}
          </p>
        </AuthCard>
      )
    case 'notFound':
    case 'expired':
    case 'canceled':
    case 'rejected':
    case 'accepted': {
      const closed = closedOf(
        view.kind,
        'preview' in view ? view.preview : undefined,
        t,
      )
      return (
        <AuthCard
          title={title}
          description={closed.description}
        >
          {/* an accepted invitation is no failure: the way in is sign-in */}
          <p
            role={view.kind === 'accepted' ? 'status' : 'alert'}
            className={view.kind === 'accepted' ? noteClass : alertClass}
          >
            {closed.text}
          </p>
          {view.kind === 'accepted' && (
            <div className={cn('mt-6')}>
              <a
                href={tenantUrl}
                className={linkClass}
              >
                {t('auth.invitation.openTenant', {
                  tenantName: view.preview.tenantName,
                })}
              </a>
            </div>
          )}
        </AuthCard>
      )
    }
    case 'signUp':
      return (
        <AuthCard
          title={title}
          description={<Offer preview={view.preview} />}
        >
          <SignUpForm
            email={view.preview.email}
            disabled={isJoining}
            onSubmit={onSignUp}
          />
          <JoinError
            error={joinError}
            tenantUrl={tenantUrl}
          />
        </AuthCard>
      )
    case 'signIn':
      return (
        <AuthCard
          title={title}
          description={<Offer preview={view.preview} />}
        >
          <SignInForm
            email={view.preview.email}
            disabled={isJoining}
            onSubmit={onSignIn}
          />
          <JoinError
            error={joinError}
            tenantUrl={tenantUrl}
          />
        </AuthCard>
      )
    case 'accept':
      return (
        <AuthCard
          title={title}
          description={<Offer preview={view.preview} />}
        >
          <p className={cn('text-sm', 'mt-6')}>
            {t('auth.invitation.signedInAs', { email: view.preview.email })}
          </p>
          <Button
            size="lg"
            className={cn('w-full', 'mt-6')}
            disabled={isJoining}
            onClick={onAccept}
          >
            {t('auth.invitation.accept')}
          </Button>
          <JoinError
            error={joinError}
            tenantUrl={tenantUrl}
          />
        </AuthCard>
      )
    case 'wrongAccount':
      return (
        <AuthCard
          title={title}
          description={<Offer preview={view.preview} />}
        >
          <p
            role="alert"
            className={alertClass}
          >
            {t('auth.invitation.wrongAccount', {
              signedInAs: view.signedInAs,
              email: view.preview.email,
            })}
          </p>
          <Button
            size="lg"
            variant="outline"
            className={cn('w-full', 'mt-6')}
            disabled={isSigningOut}
            onClick={onSignOut}
          >
            {t('auth.invitation.signOut')}
          </Button>
          {signOutError !== null && (
            <p
              role="alert"
              className={alertClass}
            >
              {t('auth.invitation.signOutFailed', {
                message: errorMessage(signOutError),
              })}
            </p>
          )}
        </AuthCard>
      )
  }
}

function Offer({ preview }: { preview: InvitationPreview }) {
  return (
    <Trans
      i18nKey="auth.invitation.offer"
      values={{ tenantName: preview.tenantName, role: preview.role }}
      components={[<strong key="tenant" />, <strong key="role" />]}
    />
  )
}

/** A new account with the invitation's email: name and password. */
function SignUpForm({
  email,
  disabled,
  onSubmit,
}: {
  email: string
  disabled: boolean
  onSubmit: (name: string, password: string) => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [mismatch, setMismatch] = useState(false)
  const [noName, setNoName] = useState(false)

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    // spaces alone are no name (`required` lets them through)
    const trimmed = name.trim()
    setNoName(trimmed === '')
    setMismatch(password !== confirmation)
    if (trimmed === '' || password !== confirmation) {
      return
    }
    onSubmit(trimmed, password)
  }

  return (
    <form onSubmit={handleSubmit}>
      <p className={cn('text-sm', 'text-muted-foreground', 'mt-6')}>
        {t('auth.invitation.signUp.intro')}
      </p>
      <EmailField email={email} />
      <Field>
        <Label htmlFor="name">{t('auth.invitation.name')}</Label>
        <Input
          id="name"
          autoComplete="name"
          maxLength={MAX_NAME_LENGTH}
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-invalid={noName}
        />
        {noName && (
          <p
            role="alert"
            className={cn('text-sm', 'text-destructive')}
          >
            {t('auth.invitation.signUp.noName')}
          </p>
        )}
      </Field>
      <Field>
        <Label htmlFor="password">{t('auth.invitation.password')}</Label>
        <InputPassword
          id="password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          maxLength={MAX_PASSWORD_LENGTH}
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
      <Field>
        <Label htmlFor="confirmation">
          {t('auth.invitation.repeatPassword')}
        </Label>
        <InputPassword
          id="confirmation"
          autoComplete="new-password"
          required
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          aria-invalid={mismatch}
        />
      </Field>
      {mismatch && (
        <p
          role="alert"
          className={cn('text-sm', 'text-destructive', 'mt-2')}
        >
          {t('auth.invitation.mismatch')}
        </p>
      )}
      <Button
        type="submit"
        size="lg"
        className={cn('w-full', 'mt-6')}
        disabled={disabled}
      >
        {t('auth.invitation.signUp.submit')}
      </Button>
    </form>
  )
}

/** The account of the invitation's email: its password. */
function SignInForm({
  email,
  disabled,
  onSubmit,
}: {
  email: string
  disabled: boolean
  onSubmit: (password: string) => void
}) {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit(password)
      }}
    >
      <p className={cn('text-sm', 'text-muted-foreground', 'mt-6')}>
        {t('auth.invitation.signInExisting.intro')}
      </p>
      <EmailField email={email} />
      <Field>
        <div className={cn('flex', 'items-center', 'justify-between')}>
          <Label htmlFor="password">{t('auth.invitation.password')}</Label>
          <Link
            to={FORGOT_PASSWORD_PATH}
            className={linkClass}
          >
            {t('auth.invitation.forgotPassword')}
          </Link>
        </div>
        <InputPassword
          id="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>
      <Button
        type="submit"
        size="lg"
        className={cn('w-full', 'mt-6')}
        disabled={disabled}
      >
        {t('auth.invitation.signInExisting.submit')}
      </Button>
    </form>
  )
}
